class MobileMenu {
    constructor(menuSelector, menuButtonSelector) {
        this.menu = document.querySelector(menuSelector);
        this.menuButton = document.querySelector(menuButtonSelector);

        if (!this.menu || !this.menuButton) {
            return;
        }

        this.init();
        this.listen();
    }

    get active() {
        return this.menu.classList.contains('is-active');
    }

    init() {
        this.setupButtonActionHandlers();
    }

    listen() {
        this.menuButton.addEventListener('click', () => {
            this.handleMenuButtonClick();
        });
    }

    setupButtonActionHandlers() {
        if (!this.menu) return;

        // Handle clicks on sub-menu toggle buttons (using event delegation)
        this.menu.addEventListener('click', (e) => {
            // Check if we're in mobile view (breakpoint-tablet is 1200px)
            if (window.innerWidth > 1200) return;

            const button = e.target.closest('button');

            if (!button) return;

            const action = button.getAttribute('data-action');

            switch (action) {
                case 'close-menu':
                    e.preventDefault();
                    this.deactivate();
                    break;

                case 'back':
                    e.preventDefault();
                    const backButton = e.target.closest('.menu-nav button');
                    const subMenu = backButton.closest('.sub-menu');

                    if (subMenu) {
                        const parentLi = subMenu.closest('li.menu-item-has-children');
                        if (parentLi) {
                            const toggleButton = parentLi.querySelector('.sub-menu-toggle');
                            this.closeSubMenu(parentLi, subMenu, toggleButton);
                        }
                    }
                    break;

                case 'open-sub-menu':
                    e.preventDefault();
                    const parentLi = e.target.closest('li.menu-item-has-children');
                    if (parentLi) {
                        const subMenu = parentLi.querySelector('.sub-menu');
                        const toggleButton = parentLi.querySelector('.sub-menu-toggle');
                        this.openSubMenu(parentLi, subMenu, toggleButton);
                    }
                    break;

                default:
                    break;
            }
        });
    }

    toggleSubMenu(parentLi, subMenu, toggleButton) {
        const isExpanded = parentLi.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
            this.closeSubMenu(parentLi, subMenu, toggleButton);
        } else {
            // Close other open sub-menus at the same level
            const siblings = Array.from(parentLi.parentElement.children);
            siblings.forEach(sibling => {
                if (sibling !== parentLi && sibling.classList.contains('menu-item-has-children')) {
                    const siblingSubMenu = sibling.querySelector('.sub-menu');
                    const siblingToggle = sibling.querySelector('.sub-menu-toggle');
                    if (siblingSubMenu && sibling.getAttribute('aria-expanded') === 'true') {
                        this.closeSubMenu(sibling, siblingSubMenu, siblingToggle);
                    }
                }
            });

            this.openSubMenu(parentLi, subMenu, toggleButton);
        }
    }

    openSubMenu(parentLi, subMenu, toggleButton) {
        // Close any nested sub-menus that might be open
        const nestedSubMenus = subMenu.querySelectorAll('.sub-menu[aria-expanded="true"]');
        nestedSubMenus.forEach(nestedSubMenu => {
            const nestedParent = nestedSubMenu.closest('li.menu-item-has-children');
            if (nestedParent) {
                const nestedToggle = nestedParent.querySelector('.sub-menu-toggle');
                this.closeSubMenu(nestedParent, nestedSubMenu, nestedToggle);
            }
        });

        parentLi.setAttribute('aria-expanded', 'true');
        subMenu.setAttribute('aria-expanded', 'true');

        // Update toggle button
        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', 'true');
            toggleButton.setAttribute('aria-label', 'Close Sub-Menu');
        }

        // Add menu-nav if it doesn't exist (back button only, no parent title)
        if (!subMenu.querySelector('.menu-nav')) {
            const menuNav = document.createElement('div');
            const title = parentLi.querySelector('a').textContent;

            menuNav.className = 'menu-nav';
            menuNav.innerHTML = `
                <button type="button" aria-label="Go back" data-action="back">
                    <i class="icon icon-chevron-left"></i>
                    <span class="text">back</span>
                </button>
                <span class="menu-title">${title}</span>
                <button type="button" aria-label="Close menu" data-action="close-menu">
                    <span class="text">Close Menu</span>
                </button>
            `;
            subMenu.insertBefore(menuNav, subMenu.firstChild);
        }
    }

    closeSubMenu(parentLi, subMenu, toggleButton) {
        parentLi.setAttribute('aria-expanded', 'false');
        subMenu.setAttribute('aria-expanded', 'false');

        // Update toggle button
        if (toggleButton) {
            toggleButton.setAttribute('aria-expanded', 'false');
            toggleButton.setAttribute('aria-label', 'Toggle Sub-Menu');
        }
    }

    closeAllSubMenus() {
        if (!this.menu) return;

        const openSubMenus = this.menu.querySelectorAll('.sub-menu[aria-expanded="true"]');
        openSubMenus.forEach(subMenu => {
            const parentLi = subMenu.closest('li.menu-item-has-children');
            if (parentLi) {
                const toggleButton = parentLi.querySelector('.sub-menu-toggle');
                this.closeSubMenu(parentLi, subMenu, toggleButton);
            }
        });
    }

    handleMenuButtonClick() {
        if (this.active) {
            this.deactivate();
        } else {
            this.activate();
        }
    }

    addMenuNav() {
        const menuNav = document.createElement('div');
        const menuItems = document.querySelector('#navbar-mobile ul');

        menuNav.className = 'menu-nav level-0';
        menuNav.innerHTML = `
            <button id="closeMenu" type="button" aria-label="Close menu" data-action="close-menu">
                <span class="text">Close Menu</span>
            </button>
        `;
        menuItems.insertBefore(menuNav, menuItems.firstChild);

        // Add drag-to-close functionality
        this.addTouchHandle();
    }

    addTouchHandle() {
        const touchHandle = document.createElement('div');
        touchHandle.className = 'touch-handle';

        // add a visible bar inside the touch handle
        const bar = document.createElement('div');
        bar.className = 'touch-handle__bar';
        touchHandle.appendChild(bar);
        
        this.menu.insertBefore(touchHandle, this.menu.firstChild);

        let startY = null;
        let dragging = false;

        const onTouchStart = (e) => {
            if (e.touches && e.touches.length === 1) {
                startY = e.touches[0].clientY;
                dragging = true;
                this.menu.style.transition = 'none';
            }
        };

        const onTouchMove = (e) => {
            if (!dragging || startY === null) return;
            const currentY = e.touches[0].clientY;
            const diffY = currentY - startY;
            if (diffY > 0) {
                this.menu.style.transform = `translateY(${diffY}px)`;
            }
        };

        const onTouchEnd = (e) => {
            if (!dragging) return;
            const endY = e.changedTouches[0].clientY;
            const diffY = endY - startY;
            this.menu.style.transition = '';
            this.menu.style.transform = '';
            dragging = false;
            startY = null;
            // If dragged down more than 100px, close the menu
            if (diffY > 100) {
                this.deactivate();
            }
        };

        touchHandle.addEventListener('touchstart', onTouchStart, { passive: true });
        touchHandle.addEventListener('touchmove', onTouchMove, { passive: false });
        touchHandle.addEventListener('touchend', onTouchEnd, { passive: true });

        // Optional: Mouse drag support for desktop
        let mouseStartY = null;
        let mouseDragging = false;

        const onMouseDown = (e) => {
            mouseStartY = e.clientY;
            mouseDragging = true;
            this.menu.style.transition = 'none';
        };

        const onMouseMove = (e) => {
            if (!mouseDragging || mouseStartY === null) return;
            const diffY = e.clientY - mouseStartY;
            if (diffY > 0) {
                this.menu.style.transform = `translateY(${diffY}px)`;
            }
        };

        const onMouseUp = (e) => {
            if (!mouseDragging) return;
            const diffY = e.clientY - mouseStartY;
            this.menu.style.transition = '';
            this.menu.style.transform = '';
            mouseDragging = false;
            mouseStartY = null;
            if (diffY > 100) {
                this.deactivate();
            }
        };

        touchHandle.addEventListener('mousedown', onMouseDown);
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    }

    activate() {
        document.body.classList.add('mobile-menu-active');
        this.menu.classList.add('is-active');
        this.menuButton.classList.add('is-active');
        this.menu.setAttribute('aria-hidden', 'false');
        this.menu.setAttribute('aria-expanded', 'true');

        if (this.menu.querySelector('.menu-nav.level-0')) return;

        this.addMenuNav();
    }

    deactivate() {
        document.body.classList.remove('mobile-menu-active');
        this.menu.classList.remove('is-active');
        this.menuButton.classList.remove('is-active');
        this.menu.setAttribute('aria-hidden', 'true');
        this.menu.setAttribute('aria-expanded', 'false');
        // Close all sub-menus when main menu closes
        this.closeAllSubMenus();
    }
}

export { MobileMenu };